/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToWishlistComponent, IWishlistActionErrorResult } from '@msdyn365-commerce/components';
import { IContext, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {
    getConfigureErrors,
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonResources,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxState,
    IQuickviewViewProps
} from '@msdyn365-commerce-modules/buybox';
import { DeliveryMode, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IQuickviewProps as IQuickviewPropsExtend } from '../definition-extensions/quickview.ext.props.autogenerated';
import { aVAGetProductNotificationEnabledAsync, avaNotifyMeRequestInsertionDetailsAsync } from '../../../modules/buybox/actions/DataActionExtension.g';
import { Button } from '@msdyn365-commerce-modules/utilities';


/**
 * RenderAddToCart.
 * @param addToCart - AddToCart.
 * @returns -- Returns.
 */
const renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

/**
 * Add to wishlist failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToWishlistFailed = (
    callbacks: IBuyboxCallbacks | undefined,
    resources: IBuyboxCommonResources,
    product: SimpleProduct | undefined
) => (result: IWishlistActionErrorResult) => {
    callbacks?.updateErrorState({
        errorHost: 'WISHLIST',
        configureErrors:
            result.status === 'MISSINGDIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param product - Product data.
 * @returns -- Returns.
 */
const renderAddtoWishlistButton = (
    props: IQuickviewViewProps,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks | undefined,
    product: SimpleProduct
): React.ReactNode => {
    const { context, resources } = props;
    const wishlists = props.data.wishlists.result;
    const isShowWishlitButtonText = true;
    return (
        <AddToWishlistComponent
            className='msc-add-to-cart-extra-actions'
            addToWishlistButtonText={resources.addToWishlistButtonText}
            removeFromWishlistButtonText={resources.removeFromWishlistButtonText}
            addToWishlistMessage={resources.addToWishlistMessage}
            removedFromWishlistMessage={resources.removedFromWishlistMessage}
            addItemToWishlistError={resources.addItemToWishlistError}
            removeItemFromWishlistError={resources.removeItemFromWishlistError}
            nameOfWishlist={resources.nameOfWishlist}
            data={{ product, wishlists }}
            context={context}
            id={props.id}
            typeName={props.typeName}
            onError={onAddToWishlistFailed(callbacks, resources, product)}
            getSelectedProduct={state.selectedProduct}
            showButtonText={isShowWishlitButtonText}
            showButtonTooltip={false}
        />
    );
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToWishlist = (
    props: IQuickviewViewProps,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks | undefined
): JSX.Element | null => {
    const product = props.productDetails?.product;
    if (!props.addToWishlist || !product) {
        return null;
    }

    const wishlistButton = renderAddtoWishlistButton(props, state, callbacks, product);
    return (
        <Node {...props.addToWishlist.ContainerProps}>
            {props.addToWishlist.errorBlock}
            {wishlistButton}
        </Node>
    );
};

/**
 * RenderQuantity.
 * @param quantity - Quantity.
 * @param quantityLimitsMessages - QuantityLimitsMessages.
 * @param props - Props.
 * @returns -- Returns.
 */
const renderQuantity = (
    quantity: IBuyboxProductQuantityViewProps,
    quantityLimitsMessages: React.ReactNode,
    props: IQuickviewViewProps
): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
    const { ContainerProps, LabelContainerProps, heading, input, errors } = quantity;
    const { unitOfMeasure } = props;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
            {unitOfMeasure}
            {quantityLimitsMessages}
            {errors}
        </Node>
    );
};

/**
 * KenderKeyInPrice.
 * @param keyInPrice - KeyInPrice.
 * @returns -- Returns.
 */
const renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};

interface IQuickViewNotifyProps {
    isNotifyEnabled: boolean;
    showNotifyForm: boolean;
    setShowNotifyForm: React.Dispatch<React.SetStateAction<boolean>>;
    handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleNotifySend: () => void;
    handleNotifyClose: () => void;
    isEmailValid: boolean;
    emailInput: string;
    setEmailInput: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * RenderBodyContent.
 * @param props - Props.
 * @returns -- Returns.
 */
const renderBodyContent = (props: IQuickviewViewProps & IQuickviewPropsExtend<{}>): JSX.Element => {
    const {
        price,
        keyInPrice,
        quantity,
        inventoryLabel,
        quantityLimitsMessages,
        loading,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
        ProductInfoContainerProps,
        MediaGalleryContainerProps,
        CarouselProps,
        addToWishlist,
        configure,
        callbacks,
        state
    } = props;
    if (loading) {
        return <>{loading}</>;
    }

    return (
        <>
            <Node {...MediaGalleryContainerProps}>
                <Node {...CarouselProps} />
            </Node>
            <Node {...ProductInfoContainerProps}>
                <Node className='msc-quickview__price-section'>
                    <Node className='msc-quickview__price-section-text'>
                        {props.resources.priceText}
                        {price}
                    </Node>
                    {inventoryLabel}
                    {addToWishlist && renderAddToWishlist(props, state, callbacks)}
                </Node>
                {configure && renderConfigure(configure)}
                {keyInPrice && renderKeyInPrice(keyInPrice)}
                {quantity && renderQuantity(quantity, quantityLimitsMessages, props)}
            </Node>
        </>
    );
};

/**
 * RenderQuickViewPopup.
 * @param props - Props.
 * @returns -- Returns.
 */
const renderQuickViewPopup = (props: IQuickviewViewProps & IQuickviewPropsExtend<{}> & IQuickViewNotifyProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
    const {
        ModalContainer,
        ModalHeaderContainer,
        ModalFooterContainer,
        ModalHeaderContent,
        ModalBodyContainer,
        addToCart,
        cartContainerProps,
        seeDetailsbutton,
        isNotifyEnabled,
        showNotifyForm,
        emailInput,
        handleEmailChange,
        setShowNotifyForm,
        handleNotifySend,
        handleNotifyClose,
        isEmailValid
    } = props;

    const extensionProps = props.productDetails?.product?.ExtensionProperties ?? [] ;
    const ecomProductName = extensionProps.find(p => p.Key === 'EcommProductName')?.Value?.StringValue;

    return (
        <Module {...ModalContainer}>
            <Node {...ModalHeaderContainer}>
                {ModalHeaderContent}
                <Node className='msc-quickview__heading'>
                    {props.state.productPrice?.['ExtensionProperties'] && (
                        <div className='msc-quickview__info desc'>
                            {props.state.productPrice?.['ExtensionProperties'][0].Value?.StringValue}
                        </div>
                    )}
                    {ecomProductName || props.title}
                    {!props.loading && !StringExtensions.isNullOrEmpty(props.state.productPrice?.ItemId) ? (
                        <Node className='msc-quickview__info'>
                            {props.resources.itemIdLabel}:
                            {props.state.productPrice?.ItemId}
                        </Node>
                    ) : (
                        ''
                    )}
                    {props.rating}
                </Node>
            </Node>
            <Node {...ModalBodyContainer}>{renderBodyContent(props)}</Node>
            <Node {...ModalFooterContainer}>
                <Node {...cartContainerProps}>
                    {addToCart && seeDetailsbutton}
                    {addToCart && renderAddToCart(addToCart)}
                    {isNotifyEnabled && (
                        <div className='ms-quickView__notifyme-container'>
                            <Button
                                className='msc-notifyme'
                                onClick={() => setShowNotifyForm(true)}
                            >
                                {props.resources.notifyMeButtonText}
                            </Button>

                            {showNotifyForm && (
                                <div className='notify-modal-overlay'>
                                    <div className="notify-modal">
                                        <div className="notify-modal-container">
                                            <h3 className="notiyme-popup-label">{props.resources.notifyMePopupText}</h3>
                                            <button
                                                className="notify-modal__close"
                                                onClick={handleNotifyClose}
                                                aria-label="Close notify me modal"
                                            />
                                        </div>

                                        <p className="notifyme-content">{props.resources.notifyMeContentText}</p>

                                        <label htmlFor="notify-email" className='notify-label'>
                                            {props.resources.notifyMeEmailLabel}
                                        </label>
                                        <input
                                            id="notify-email"
                                            className='notify-email-input'
                                            type="email"
                                            value={emailInput}
                                            onChange={handleEmailChange}
                                            required
                                        />

                                        <div>
                                            <Button onClick={handleNotifySend} className="submit-button"
                                                disabled={!isEmailValid}
                                                aria-disabled={!isEmailValid}
                                                >
                                                {props.resources.notifyMeSendButton}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Node>
            </Node>
        </Module>
    );
};

/**
 * Functional component which renders quick view button and popup based by the given props file.
 * @param props - Configuration for the quick view component.
 * @returns - Functional component of the quick view.
 */
export const QuickViewFunctionalComponent: React.FC<IQuickviewViewProps & IQuickviewPropsExtend<{}>> = (
    props: IQuickviewViewProps & IQuickviewPropsExtend<{}>
) => {
    const { quickView, quickViewButton, isModalOpen } = props;
    const quickViewUpdatedProps: IModuleProps = { ...quickView, title: props.resources.quickViewbuttonText };
    
    const [showNotifyForm, setShowNotifyForm] = React.useState(false);
    const [emailInput, setEmailInput] = React.useState(props.context.request.user?.emailAddress || '');
    const [isEmailValid, setIsEmailValid] = React.useState(false);
    const [isNotifyEnabled, setIsNotifyEnabled] = React.useState(false);

    React.useEffect(() => {
        const userEmail = props.context.request.user.emailAddress;

        if (userEmail) {
            const valid = validateEmail(userEmail);
            setIsEmailValid(valid);
            setEmailInput(userEmail);
        }
        
        const productId = props.productDetails?.product?.RecordId;
        const productAvailableQuantity = props.productDetails?.productAvailableQuantity;
        let stockCode = '';

        const shippingInventory = productAvailableQuantity?.find(
            inv => inv.deliveryType === DeliveryMode.shipping
        );

        if (shippingInventory) {
            stockCode = shippingInventory.StockLevelCode ?? '';
        }

        if (shippingInventory?.StockLevelCode && typeof productId === 'number') {
            aVAGetProductNotificationEnabledAsync(
                { callerContext: { requestContext: props.context.request } },
                productId,
                stockCode
            )
                .then(response => {
                    console.log('Full Notify Me API response:', response);
                    setIsNotifyEnabled(response === true);
                })
                .catch(error => {
                    console.error('Failed to fetch notification flag:', error);
                });
        }
    }, [props.productDetails?.product?.RecordId, props.inventoryLabel]);

    const handleNotifyClose = () => {
        setShowNotifyForm(false);
    };

    const validateEmail = (email: string): boolean => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const email = e.target.value;
        setEmailInput(email);
        const valid = validateEmail(email);
        setIsEmailValid(valid);
    };

    const handleNotifySend = async () => {
        if (!validateEmail(emailInput)) {
            console.log('Email invalid, not submitting');
            return;
        }
        const customer = props.context.request.user;
        const product = props.productDetails?.product;
        const itemId = props.state.productPrice?.ItemId;
        const itemName = product?.Name;
        const accountId = customer?.customerAccountNumber;
        const accountName = customer?.name;
        const context: IContext = {
            ...props.context,
            callerContext: {
                requestContext: props.context.request
            }
        };
        
        const language = props.context.request.locale?.toLowerCase() || 'en-us'; // current language
        const basePath = window.location.pathname.split('/')[1]; // 'medical' or 'fertility'
        const productNameSlug = itemName?.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
        const pdpUrl = `/${basePath}/${language}/${productNameSlug}/${product?.RecordId}.p`;
         // Update the address bar only (no redirect)
        window.history.pushState({}, '', pdpUrl);

        const payload = {
            AccountId: accountId,
            AccountName: accountName,
            EmailId: emailInput,
            ItemId: itemId,
            ItemName: itemName,
            UrlPDP: `${window.location.origin}${pdpUrl}`,
            RequestDate: new Date(),
            LanguageID: props.context.request.locale || 'en-us'     // Language
        };

        try {
            const response = await avaNotifyMeRequestInsertionDetailsAsync(context, payload);

            if (response?.Result === true) {
                console.log('Notify Me request submitted successfully');
            } else {
                console.error('Notify Me request failed:', response);
            }
        } catch (error) {
            console.error(error);
            alert('Error sending notification request');
        }
        setShowNotifyForm(false);

    };

    return (
        <Module {...quickViewUpdatedProps}>
            {quickViewButton}
            {isModalOpen && renderQuickViewPopup({
                ...props,
                showNotifyForm,
                setShowNotifyForm,
                handleNotifySend,
                handleNotifyClose,
                isNotifyEnabled,
                isEmailValid,
                handleEmailChange,
                emailInput,
                setEmailInput
            })}
        </Module>
    );
};

export default QuickViewFunctionalComponent;
